import { AppTitle } from '@/components/atoms/AppTitle/AppTitle'
import { MobileWalletButton } from '@/components/molecules/MobileWalletButton/MobileWalletButton'
import { AuthLostWallet } from '@/features/Auth/ui/AuthLostWallet/AuthLostWallet'
import { WalletButtons } from '@/features/Auth/ui/AuthWallets/WalletButtons'
import { useModal } from '@/features/Modal/Modal.hooks'
import { useAppWallet } from '@/shared/wallet/useAppWallet'
import { WALLET_DEFINITIONS } from '@/shared/wallet/walletDefinitions'
import { Wallets, walletsMap } from '@/shared/constants/wallets'
import { trackUserAction } from '@/shared/helpers/sentry'
import { useAuthStore } from '@/store'
import { Flex, message } from 'antd'
import React, { useCallback, useEffect } from 'react'
import { isBrowser, isMobile } from 'react-device-detect'
import styles from './AuthWallets.module.scss'
import { AuthStep } from '@/features/Auth/auth.types'

export function AuthWallets({ setStep, setSelectedWallet, type }: { setSelectedWallet: Function; setStep: (step: AuthStep) => void; type?: 'add' }) {
  const selectedWalletType = useAuthStore((state) => state.selectedWalletType)
  const setWalletType = useAuthStore((state) => state.setSelectedWalletType)
  const selectedPublicKey = useAuthStore((state) => state.selectedPublicKey)
  const setSelectedPublicKey = useAuthStore((state) => state.setSelectedPublicKey)
  const wallet = useAppWallet(selectedWalletType)
  const walletConnectWallet = useAppWallet(walletsMap[Wallets.WalletConnect])
  const { createModal } = useModal()

  const handleLostWallet = useCallback((e) => {
    e.preventDefault()
    createModal(<AuthLostWallet />)
  }, [createModal])

  const handleWalletClick = useCallback(async (walletSelected: string) => {
    trackUserAction('User selected wallet', { wallet: walletSelected, flow: type === 'add' ? 'add-wallet' : 'sign-in' })

    if (wallet.connected && type === 'add') {
      await wallet.disconnect()
    }

    setSelectedWallet(walletSelected)
    setWalletType(walletSelected)

    if (walletSelected === walletsMap[Wallets.WalletConnect]) {
      if (type === 'add' && walletConnectWallet.publicKey && walletConnectWallet.publicKey === selectedPublicKey) {
        message.info('You trying to add same public key, please change it and try again.')
        return
      }

      if (type === 'add' && walletConnectWallet.connected) {
        await walletConnectWallet.disconnect()
      }

      await walletConnectWallet.connect()
      return
    }

    setStep(AuthStep.WALLET_CONNECT)
  }, [selectedPublicKey, setSelectedWallet, setStep, setWalletType, type, wallet, walletConnectWallet])

  useEffect(() => {
    if (selectedWalletType === walletsMap[Wallets.WalletConnect] && walletConnectWallet.connected && walletConnectWallet.publicKey) {
      setSelectedPublicKey(walletConnectWallet.publicKey)
      setStep(AuthStep.WALLET_CONNECTED)
    }
  }, [selectedWalletType, walletConnectWallet.connected, walletConnectWallet.publicKey, setSelectedPublicKey, setStep])

  const handleWalletLink = (walletName: string) => {
    if (walletName === 'Phantom') {
      window.open(`https://phantom.app/ul/browse/${encodeURIComponent('https://app.godefi.me')}?ref=${encodeURIComponent('https://app.godefi.me')}`, '_blank')
    }

    if (walletName === 'Solflare') {
      window.open(`https://solflare.com/ul/v1/browse/${encodeURIComponent('https://app.godefi.me')}?ref=${encodeURIComponent('https://app.godefi.me')}`, '_blank')
    }
  }

  const title = type && type === 'add'
    ? 'Select the wallet you want to add to the GoDefi app'
    : <>Select the wallet you <br/>would like to use with the <br/>GoDefi app</>

  useEffect(() => {
    if (type !== 'add' && wallet.connected) {
      wallet.disconnect()
    }
    // Mount-only: clears a stale prior session on arrival. Must not depend on
    // wallet.connected — WalletConnect now connects in place on this screen, and
    // re-running this on that transition would disconnect the fresh session immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Flex vertical justify="center" align="center" className="mobileContainer">
      <Flex className="w-full" flex={1} vertical justify="center" gap={50}>
        <AppTitle centered text={title} />
        <Flex className="w-full" vertical gap={16}>
          <>
            <WalletButtons
              handleWalletClick={handleWalletClick}
              handleWalletLink={handleWalletLink}
              isBrowser={isBrowser}
              isMobile={isMobile}
            />
            {WALLET_DEFINITIONS.map(({ kind, label }) => (
              <MobileWalletButton
                key={kind}
                icon={kind === Wallets.WalletConnect ? '' : label}
                onClick={() => handleWalletClick(label)}
                title={kind === Wallets.WalletConnect ? 'Select more wallets' : `Connect to ${label}`}
              />
            ))}
          </>
        </Flex>
      </Flex>
      <p className={styles.storeText}>We do not store the private keys of your wallets</p>
      {type !== 'add' && <a onClick={handleLostWallet} className={styles.lostAccess}>Lost access to your wallet?</a>}
    </Flex>
  )
}
